






























  import { Component, mixins } from 'nuxt-property-decorator'

  import { namespace } from 'vuex-class'
  import { Categories, CMS, Plugins, Catalog } from '@one/types'
  import SeoMethodsMixin, { DIVIDER } from '../mixins/SeoMethodsMixin'
  import Category = Categories.Category;
  import StaticPageDto = CMS.Responses.StaticPageDto
  import { ExtendedComponent } from '~/store/plugins'
  import IFrameShopPlugin = Plugins.IFrameShopPlugin
  import ContentStaticPageDto = CMS.StaticPages.ContentStaticPageDto
  import { createStaticPageCmsComponents, CmsAsyncComponents } from '~/utils/cmsComponents'
  import SiteSections = CMS.SiteSections

  const cms = namespace('cms')
  const categories = namespace('categories')
  const products = namespace('products')
  const plugins = namespace('plugins')
  const routeStore = namespace('route')
  const brands = namespace('brands')

  @Component({
    scrollToTop: true,
    middleware: ['isAuthenticatedOrPlatformOpened'],
    watchQuery: ['filtersQuery', 'rangeFiltersQuery', 'page', 'rows', 'sortCriteria', 'price'],
    transition(_to: any, from: any): string {
      if (from && (from.name.includes('search') || from.name === 'all')) {
        return 'no-transition'
      }
      return 'fade'
    },
    components: {
      OneSiteSectionsView: () => import('~/components/organisms/siteSections/OneSiteSectionsView.vue'),
      OneCatalogViewList: () => import('~/components/organisms/list/OneCatalogViewList.vue'),
      OneCmsGridRender: () => import('~/components/molecules/renders/OneCmsGridRender.vue'),
      OneExternalIframePlugin: () => import('~/components/molecules/plugins/OneExternalIframePlugin.vue'),
      Error: () => import('~/layouts/error.vue'),
    },
  })
    export default class GenericPage extends mixins<SeoMethodsMixin>(SeoMethodsMixin) {
    name: string = 'generic-page'

    @routeStore.State path: any;
    @cms.State(state => state.staticPages) staticPages!: Record<string, StaticPageDto>;
    @cms.State(state => state.siteSections) siteSections!: Record<string, SiteSections.SiteSection>;
    @cms.Getter logo: any
    @categories.Getter getCategoryByUrl: any;
    @categories.Getter getSelectedCategory: any
    @categories.Getter getCategory: any
    @categories.State selectedCategoriesTree: any
    @products.Mutation('CLEAR_SELECTED_FILTERS') clearSelectedFilters: any;
    @plugins.Getter getRoutableComponentBySlug: any;
    @brands.Getter getBrandByName: any;
    @brands.Action selectBrand: any;
    @brands.Action clearSelectedBrand: any;

    notFoundError: any = {
      statusCode: 404,
      err: {},
    }

    availableComponents: CmsAsyncComponents = createStaticPageCmsComponents()

    mainCatalogPageClass = 'one-catalog-page'
    mainPluginPageClass = 'one-external-plugin-page'

    head() {
      return {
        title: this.structuredSeoTitle(DIVIDER.DOT, [this.filtersSeo, this.titleSeoPage]),
        meta: [
          {
            hid: 'description',
            name: 'description',
            content: this.structuredSeoTitle(DIVIDER.DASH, this.descriptionSeoPage),
          },
          {
            hid: 'robots',
            name: 'robots',
            content: this.pageRobotsValue,
          },
          { property: 'og:image', content: this.logo?.img ?? '', hid: 'og:image' },
          { property: 'og:title', content: this.structuredSeoTitle(DIVIDER.DOT, [this.filtersSeo, this.titleSeoPage]) ?? '' },
          { property: 'og:description', content: this.structuredSeoTitle(DIVIDER.DASH, this.descriptionSeoPage) ?? '' },
        ],
      }
    }

    get staticPage(): StaticPageDto {
      return this.staticPages[this.path.substring(1)]
    }

    firstPathSegment(path: string): string {
      const normalizedPath = path.startsWith('/') ? path.substring(1) : path
      return normalizedPath.split('/')[0] || ''
    }

    get siteSection(): SiteSections.SiteSection {
      return this.siteSections[this.firstPathSegment(this.path)]
    }

    get pluginComponent(): ExtendedComponent<IFrameShopPlugin> {
      return this.getRoutableComponentBySlug(this.path.substring(1))
    }

    get titleSeoPage(): string | null {
      if (this.pluginComponent) { return this.pluginComponent.raw.page.title }
      if (this.staticPage) { return this.staticPage.seo.title }
      if (this.isCategoryView) {
        return this.getSelectedCategory?.seo?.title || this.getSelectedCategory?.name || null
      }
      return null
    }

    get hiddenH1Value() {
      if (this.staticPage) { return this.staticPage.seo.h1 }
    }

    get descriptionSeoPage(): Array<string> {
      if (this.staticPage) { return [this.staticPage.seo.description] }
      if (this.titleSeoPage) {
        const categoryDescription = this.getSelectedCategory?.seo?.description
        const defaultDescription = !categoryDescription && [this.defaultSeoData.description]
        return [
          this.structuredSeoTitle(DIVIDER.DASH, [categoryDescription || this.titleSeoPage, this.filtersSeo, defaultDescription]),
        ].filter(Boolean)
      } else {
        return [this.defaultSeoData.description]
      }
    }

    get pageRobotsValue(): string {
      if (this.staticPage) { return this.staticPage.seo.robots }
      return this.categoryRobotsSeo
    }

    get categoryRobotsSeo(): string {
      const categoryRobots = this.robotsSeo(this.getSelectedCategory?.seo)
      return this.isCategoryView && categoryRobots.length >= 2 ? categoryRobots : this.defaultSeoRobots
    }

    get isCategoryView(): boolean {
      return this.getCategoryByUrl(this.path)
    }

    get brandRouteMatch(): {
      brand: Catalog.BrandAndSeriesTreeItem,
      series?: Catalog.BrandAndSeriesTreeItem['series'][number],
      category?: Category,
    } | null {
      const rawPath = (this.path || '').replace(/^\/+|\/+$/g, '')
      if (!rawPath) { return null }
      const rawSegments = rawPath.split('/').filter(Boolean)
      const segments = rawSegments.map(segment => decodeURIComponent(segment))
      const brand = this.getBrandByName(segments[0])
      if (!brand) { return null }
      if (segments.length === 1) {
        return { brand }
      }
      const series = brand.series.find(item => item.name === segments[1])
      if (series) {
        if (segments.length === 2) {
          return { brand, series }
        }
        const categoryPath = `/${rawSegments.slice(2).join('/')}`
        const category = this.getCategoryByUrl(categoryPath)
        if (!category) { return null }
        return { brand, series, category }
      }
      // No matching series – treat the remaining segments as a category path under the brand
      const categoryPathNoSeries = `/${rawSegments.slice(1).join('/')}`
      const categoryNoSeries = this.getCategoryByUrl(categoryPathNoSeries)
      if (!categoryNoSeries) { return null }
      return { brand, category: categoryNoSeries }
    }

    get selectedBrand(): Catalog.BrandAndSeriesTreeItem | undefined {
      return this.brandRouteMatch?.brand
    }

    get selectedSeries(): Catalog.BrandAndSeriesTreeItem['series'][number] | undefined {
      return this.brandRouteMatch?.series
    }

    get isBrandView(): boolean {
      return !!this.brandRouteMatch
    }

    get brandListingTitle(): string | undefined {
      return this.selectedSeries?.name || this.selectedBrand?.brand?.name
    }

    get brandBasePath(): string {
      if (!this.selectedBrand) { return '' }
      const brandPath = `/${encodeURIComponent(this.selectedBrand.brand.name)}`
      if (!this.selectedSeries) { return brandPath }
      return `${brandPath}/${encodeURIComponent(this.selectedSeries.name)}`
    }

    get listingTitle(): string | undefined {
      const selectedCategory = this.getSelectedCategory && this.getSelectedCategory.name
      return selectedCategory && this.isCategoryView ? `${selectedCategory}` : undefined
    }

    destroyed() {
      if (this.isBrandView) {
        try { this.clearSelectedBrand() } catch {}
      }
      if (this.isCategoryView) {
        this.$plugins.onUnmounted(this.mainCatalogPageClass)
      } else if (this.pluginComponent) {
        this.$plugins.onUnmounted(this.mainPluginPageClass)
      }
    }

    mounted() {
      if (this.isCategoryView) {
        this.$plugins.onMounted(this.mainCatalogPageClass, this)
      } else if (this.pluginComponent) {
        this.$plugins.onMounted(this.mainPluginPageClass, this)
      }
    }

    async fetch({
      app, store, route, redirect, error,
    }: any) {
      const firstPathSegment = (path: string): string => {
        const normalizedPath = path.startsWith('/') ? path.substring(1) : path
        return normalizedPath.split('/')[0] || ''
      }
      const handleGridStaticPage = (gridStaticPage?: ContentStaticPageDto | undefined) => {
        store.commit('layout/TOGGLE_BREADCRUMBS', false)
        if (gridStaticPage?.seo?.title && gridStaticPage?.slug) {
          store.commit('layout/SET_BREADCRUMBS', [{
            name: gridStaticPage.seo.title,
            link: `/${gridStaticPage.slug}`,
          }])
          store.commit('layout/TOGGLE_BREADCRUMBS', true)
        }
      }
      const buildFiltersQueryWithBrandAndSeries = (brandName: string, seriesName?: string): Array<string> => {
        const raw = route.query.filtersQuery
        const current: Array<string> = Array.isArray(raw)
          ? [...raw]
          : (raw ? [raw as string] : [])
        const brandEntry = `brand:${brandName}`
        // Keep a single brand/productSeries pair in URL filters for dedicated brand pages.
        const without = current.filter(item => !!item && !item.startsWith('brand:') && !item.startsWith('productSeries:'))
        without.push(brandEntry)
        if (seriesName) {
          without.push(`productSeries:${seriesName}`)
        }
        return without
      }
      const handleBrandPage = async (
        brand: Catalog.BrandAndSeriesTreeItem,
        series?: Catalog.BrandAndSeriesTreeItem['series'][number],
        category?: Category,
      ) => {
        const currentPriceType: string = store.getters['layout/priceType']
        const setCatalogLoading = () => {
          if (process.client) {
            app.wait.initialized && app.wait.start('catalog-loading')
          }
        }
        const clearCatalogLoading = () => {
          if (process.client) {
            setTimeout(() => {
              app.wait.initialized && app.wait.end('catalog-loading')
            }, 250)
          }
        }
        const addedPriceParam = (price: string | null) => {
          if (!(app.$utils.isAnonymousAndPurchaseDisabled || app.$utils.isB2BAndPlatformIsNotOpened) || !price) {
            store.commit('products/SET_PRICE', price)
          }
        }
        const validPrice = () => {
          const { price } = route.query
          if (price) {
            const parsedPrice: [number, number] | null = app.$routes.parsePriceString(price)
            if (parsedPrice) {
              const [min, max] = parsedPrice
              if (min <= max && (price as string).includes(currentPriceType)) {
                return addedPriceParam(price as string)
              }
            }
          }
          addedPriceParam(null)
        }
        const filtersQuery = buildFiltersQueryWithBrandAndSeries(brand.brand.id, series?.id)
        const queryWithBrand = { ...route.query, filtersQuery }
        const fetchBrandData = async () => {
          try {
            setCatalogLoading()
            const res: { ids: Array<string>, categorySelected: boolean } =
              await store.dispatch('products/fetchProducts', queryWithBrand)
            await store.dispatch('products/fetchFilters', queryWithBrand)
            if (app.$utils.isPurchaseEnabledOrAuthenticated) {
              await Promise.all([
                store.dispatch('stocks/fetchStocksForProductsAndWarehouses', {
                  skus: res.ids,
                  warehouses: store.state.stocks.warehouses.allIds,
                }),
                store.dispatch('products/fetchPricesForProducts', res.ids),
              ])
            }
            clearCatalogLoading()
          } catch (err) {
            app.$logger.warn(err)
          }
        }
        const defaultRows = 15
        const defaultPage = 1
        const defaultSortCriteria = store.getters['products/getDefaultSortForCatalogBrowse']
        validPrice()
        const encodedBrand = encodeURIComponent(brand.brand.name)
        const brandPath = `/${encodedBrand}`
        const seriesPath = series ? `${brandPath}/${encodeURIComponent(series.name)}` : null
        store.commit('brands/SELECT_BRAND', brand)
        if (category) {
          await store.dispatch('categories/selectCategory', category.id)
        } else {
          store.commit('categories/SELECT_CATEGORY', null)
          store.commit('categories/SET_SELECTED_CATEGORIES_TREE', [])
        }
        store.commit('layout/TOGGLE_BREADCRUMBS', true)
        const breadcrumbs = [{
          name: brand.brand.name,
          link: brandPath,
        }]
        if (series && seriesPath) {
          breadcrumbs.push({
            name: series.name,
            link: seriesPath,
          })
        }
        if (category && seriesPath) {
          const categoryBreadcrumbs = app.$routes.getCategoryBreadcrumbs(category.id)
          breadcrumbs.push(...categoryBreadcrumbs.map((crumb: any) => ({
            name: crumb.name,
            link: `${seriesPath}${crumb.link}`,
          })))
        } else if (category) {
          const categoryBreadcrumbs = app.$routes.getCategoryBreadcrumbs(category.id)
          breadcrumbs.push(...categoryBreadcrumbs.map((crumb: any) => ({
            name: crumb.name,
            link: `${brandPath}${crumb.link}`,
          })))
        }
        store.commit('layout/SET_BREADCRUMBS', breadcrumbs)
        store.commit('products/SET_PAGE_SIZE', Number(route.query.rows || defaultRows))
        store.commit('products/SET_PAGE_NUMBER', Number(route.query.page || defaultPage))
        store.commit('products/SET_SORT_CRITERIA', route.query.sortCriteria || defaultSortCriteria)
        if (process.client) {
          fetchBrandData()
        } else {
          await fetchBrandData()
        }
        return Promise.resolve()
      }
      const handleCatalogPage = async (category: Category) => {
        const currentPriceType: string = store.getters['layout/priceType']
        const setCatalogLoading = () => {
          if (process.client) {
            app.wait.initialized && app.wait.start('catalog-loading')
          }
        }
        const clearCatalogLoading = () => {
          if (process.client) {
            setTimeout(() => {
              app.wait.initialized && app.wait.end('catalog-loading')
            }, 250)
          }
        }
        const addedPriceParam = (price: string | null) => {
          if (!(app.$utils.isAnonymousAndPurchaseDisabled || app.$utils.isB2BAndPlatformIsNotOpened) || !price) {
            store.commit('products/SET_PRICE', price)
          }
        }
        const redirectDeletePrice = () => {
          const { query } = route
          delete query.price
          addedPriceParam(null)
          return redirect(301, route.path, query)
        }
        const validPrice = () => {
          const { price } = route.query
          if (price) {
            const parsedPrice: [number, number] | null = app.$routes.parsePriceString(price)
            if (!parsedPrice) {
              addedPriceParam(null)
              return redirectDeletePrice()
            }
            const [min, max] = parsedPrice
            if (min <= max && price.includes(currentPriceType)) {
              return addedPriceParam(price)
            }
            return redirectDeletePrice()
          }
          addedPriceParam(null)
          return null
        }
        const fetchCatalogData = async () => {
          try {
            setCatalogLoading()
            const res: { ids: Array<string>, categorySelected: boolean } = await store.dispatch('products/fetchProducts', route.query)
            await store.dispatch('products/fetchFilters', route.query)
            if (res.categorySelected) {
              if (app.$utils.isPurchaseEnabledOrAuthenticated) {
                await Promise.all([
                  store.dispatch('stocks/fetchStocksForProductsAndWarehouses', {
                    skus: res.ids,
                    warehouses: store.state.stocks.warehouses.allIds,
                  }),
                  store.dispatch('products/fetchPricesForProducts', res.ids),
                ])
              }
            }
            clearCatalogLoading()
          } catch (error) {
            app.$logger.warn(error)
          }
        }
        const defaultRows = 15
        const defaultPage = 1
        const defaultSortCriteria = store.getters['products/getDefaultSortForCatalogBrowse']
        validPrice()
        await store.dispatch('categories/selectCategory', category.id)
        store.commit('layout/TOGGLE_BREADCRUMBS', true)
        store.commit('layout/SET_BREADCRUMBS', app.$routes.getCategoryBreadcrumbs())
        store.commit('products/SET_PAGE_SIZE', Number(route.query.rows || defaultRows))
        store.commit('products/SET_PAGE_NUMBER', Number(route.query.page || defaultPage))
        store.commit('products/SET_SORT_CRITERIA', route.query.sortCriteria || defaultSortCriteria)
        if (process.client) {
          fetchCatalogData()
        } else {
          await fetchCatalogData()
        }
        return Promise.resolve()
      }
      try {
        if (route.query.q) {
          return redirect(301, '/search', route.query)
        }
        const pluginComponent: ExtendedComponent<IFrameShopPlugin> | null = store.getters['plugins/getRoutableComponentBySlug'](route.path)
        if (pluginComponent) {
          return handleGridStaticPage()
        }
        const brandsStore = store.state.brands
        if (!brandsStore.brandsTree.length) {
          try { await store.dispatch('brands/fetchBrandsTree') } catch (e) { app.$logger?.warn?.(e) }
        }
        const rawBrandPath = (route.path || '').replace(/^\/+|\/+$/g, '')
        const rawSegments = rawBrandPath ? rawBrandPath.split('/').filter(Boolean) : []
        const decodedSegments = rawSegments.map(segment => decodeURIComponent(segment))
        const matchedBrand = decodedSegments.length
          ? store.getters['brands/getBrandByName'](decodedSegments[0])
          : undefined
        if (matchedBrand) {
          if (decodedSegments.length === 1) {
            return handleBrandPage(matchedBrand)
          }
          const matchedSeries = matchedBrand.series.find(item => item.name === decodedSegments[1])
          if (matchedSeries) {
            if (decodedSegments.length === 2) {
              return handleBrandPage(matchedBrand, matchedSeries)
            }
            const categoryPath = `/${rawSegments.slice(2).join('/')}`
            const categoryFromBrandPath = store.getters['categories/getCategoryByUrl'](categoryPath)
            if (categoryFromBrandPath) {
              return handleBrandPage(matchedBrand, matchedSeries, categoryFromBrandPath)
            }
          } else {
            // No matching series – treat remaining segments as a category path under the brand
            const categoryPathNoSeries = `/${rawSegments.slice(1).join('/')}`
            const categoryUnderBrand = store.getters['categories/getCategoryByUrl'](categoryPathNoSeries)
            if (categoryUnderBrand) {
              return handleBrandPage(matchedBrand, undefined, categoryUnderBrand)
            }
          }
        }
        // Not a brand page – ensure any previously selected brand is cleared
        store.commit('brands/CLEAR_SELECTED_BRAND')
        const category: Category | null = store.getters['categories/getCategoryByUrl'](route.path)
        if (category) {
          return handleCatalogPage(category)
        }
        const gridStaticPage = route.path.substring(1) ? await store.dispatch('cms/fetchStaticPageBySlug', route.path.substring(1)) : null
        if (gridStaticPage) {
          handleGridStaticPage(gridStaticPage)
          const isSiteSection = store.getters['cms/siteSections']
          if (isSiteSection) {
            const sectionSlug = firstPathSegment(route.path)
            const section = isSiteSection[sectionSlug]
            if (section) {
              const crumbs = [
                {
                  name: section.name,
                  link: `/${section.slug}`,
                },
              ]
              const subSectionSlug = route.path.substring(1)
              if (subSectionSlug) {
                const subSection = section.items.find((item: SiteSections.SiteSubSection) => subSectionSlug.includes(item.slug))
                if (subSection) {
                  crumbs.push({
                    name: subSection.name,
                    link: `/${subSection.slug}`,
                  })
                } else if (gridStaticPage?.seo?.title) {
                  crumbs.push({
                    name: gridStaticPage.seo.title,
                    link: route.path,
                  })
                }
              }
              store.commit('layout/SET_BREADCRUMBS', crumbs)
              store.commit('layout/TOGGLE_BREADCRUMBS', true)
            }
          }
          return Promise.resolve()
        }
        return store.commit('layout/TOGGLE_BREADCRUMBS', false)
      } catch (err: any) {
        return error({
          statusCode: err.response?.status,
          message: err.response?.data,
          err: err,
        })
      }
    }
  }
